import { MenuItem } from "@mui/material";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import { Proposal } from "generated/graphql";
import useDeleteProposal from "hooks/proposals/useDeleteProposal";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import { useCallback, useState } from "react";

export default function DeleteProposalMenuItem({
  closeMenu,
  id,
  onDeleteStart,
  onDeleteSuccess,
  onDeleteSettled,
}: {
  closeMenu: () => void;
  id: Proposal["id"];
  onDeleteStart?: (id: Proposal["id"]) => void;
  onDeleteSuccess?: (id: Proposal["id"]) => void;
  onDeleteSettled?: (id: Proposal["id"]) => void;
}) {
  const [deleteProposal, { isSuccess, isAlreadyDeletedError }] = useDeleteProposal();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const acceptFn = useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true);
    onDeleteStart?.(id);
    const result = await deleteProposal({ variables: { id } });
    const deleted = isSuccess(result?.data) || isAlreadyDeletedError(result?.error ?? result);

    if (deleted) {
      onDeleteSuccess?.(id);
      closeMenu();
    } else {
      onDeleteSettled?.(id);
    }
    setIsSubmitting(false);
    return deleted;
  }, [closeMenu, deleteProposal, id, isAlreadyDeletedError, isSuccess, onDeleteSettled, onDeleteStart, onDeleteSuccess]);

  const cancelFn = useCallback(async () => {
    if (isSubmitting) {
      return false;
    }
    closeMenu();
    return true;
  }, [closeMenu, isSubmitting]);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });
  return (
    <>
      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        content="Are you sure you want to delete this Proposal?"
        isAcceptDisabled={isSubmitting}
        isCancelDisabled={isSubmitting}
        acceptText={isSubmitting ? "Deleting..." : "Accept"}
      />
      <MenuItem data-test-id="proposal-delete" disabled={isSubmitting} onClick={() => setOpen(true)}>
        Delete
      </MenuItem>
    </>
  );
}
